import {
  ParamListBase,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { Field, FormikErrors, Formik } from 'formik';
import jwt_decode from 'jwt-decode';
import React, { useEffect, useRef, useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { TextInput, TouchableOpacity, Linking } from 'react-native';

import { Alert } from '@components/Alert/index.web';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';
import TextField from '@components/shared/TextField/TextField';
import { CheckBoxButton } from '@components/Web/CheckBoxButton';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import { useNotification } from '@hooks/useNotification.web';
import { AuthFormValues } from '@navigation/auth/log-in-stack';
import authSchema from '@schemas/authSchema';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import { useMixpanel } from '@src/utils/mixpanel';
import theme from '@themes/theme';
import { logEvent } from '@utils/eventLogger';
import { getApiConfig } from '@utils/getApiConfig';
import { stashGenericInvitationForLogin } from '@utils/invitationWebUrl';
import { parseAuthResponse } from '@utils/rateLimit';
import { requestPasswordReset } from '@utils/requestPasswordReset';
import { AsyncStorage, SecurityStorage } from '@utils/storage';

type UserInfo = {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  provider?: string;
  identity_token?: string;
};

export const EmailLogin: React.FC<ParamListBase> = () => {
  const navigation = useNavigation();
  const [loading, setLoading] = useState<boolean>(false);
  const {
    prompt,
    userInfo,
    setUserDetail,
    loading: googleAuthLoading,
  } = useGoogleAuth();
  const [appleAuthLoading, setAppleAuthLoading] = useState<boolean>(false);
  const apiConfig = getApiConfig();
  const { setAuthToken } = useAuthContext();
  const { trackEvent } = useMixpanel();
  const { t } = useTranslation('models');
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [errorName, setErrorName] = useState('');

  const [keepsigned, setKeepsigned] = useState(true);
  const [resetLoadingState, setResetLoadingState] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Readonly (what `editable={false}` maps to on web) intentionally stays
  // focusable so users can still select/copy text — but a field the user
  // was already focused in keeps its focus ring through submit unless we
  // blur it explicitly, which reads as "still editable" (TA-10089 QA).
  const emailInputRef = useRef<TextInput>(null);
  const passwordInputRef = useRef<TextInput>(null);

  const route = useRoute();
  const fromReset = route?.params?.fromReset ?? false;

  const [showMessage, setShowMessage] = useState(fromReset);

  const initialValues: AuthFormValues = {
    email: '',
    password: '',
  };

  const { registerForPushNotificationsAsync } = useNotification();
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await registerForPushNotificationsAsync();
        setDeviceToken(token || null);
      } catch (error) {
        console.error('Failed to get device token', error);
        setDeviceToken(null);
      }
    };

    fetchToken();
  }, []);

  useEffect(() => {
    if (userInfo?.fromSignUp && userInfo?.fromSignUp === true) return;
    userInfo && setLoading(true);
    userInfo && verifyUserEmail(userInfo);
  }, [userInfo]);

  const verifyUserEmail = async (userInfo: UserInfo) => {
    const verifyUser = `/api/v3/verify_user.json`;
    const userPayload = {
      user: {
        email: userInfo?.email?.toLowerCase(),
        provider: userInfo?.provider,
        uid: userInfo?.id,
        is_verified: false,
        firebase_token: deviceToken,
        platform: 'web',
        // TA-10975: proves the caller holds a live Apple/Google credential
        // for this account — see AppleIdentityTokenVerifier /
        // GoogleIdentityTokenVerifier on the backend.
        identity_token: userInfo?.identity_token,
      },
    };
    const authEndpoint = `${apiConfig.apiUrl}${verifyUser}`;
    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userPayload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        setLoading(false);
        if (response?.error) {
          if (response.auth_provider) {
            setShowProviderModal(true);
            setErrorName(response.error);
          } else {
            navigation.navigate('register', {
              socialSignUp: true,
            });
          }
        } else if (response.user) {
          if (
            userInfo?.provider === 'Apple' &&
            (!response.user.phone_number || response.user.phone_number === '')
          ) {
            // Apple login/signup need to input phone number after create account
            navigation.navigate('register', {
              socialSignUp: true,
            });
          } else {
            try {
              await AsyncStorage.setItem(
                'keepSignedIn',
                keepsigned ? 'true' : 'false'
              );
              window.sessionStorage.setItem('setKeepSignedIn', '1');
              // A generic Contacts invite is accepted in AppStack (TA-11035).
              stashGenericInvitationForLogin();
              // Preserve team invitation tokens so app-stack can accept the
              // invite after login. Only clear for unrelated social logins.
              if (window.sessionStorage.getItem('link_type') !== 'team') {
                window.sessionStorage.setItem('invitation_token', '');
              }
              await SecurityStorage.setItem(
                'authToken',
                response.user.api_tokens[0].token
              );
            } catch (e) {
              console.log('An error happened, ', e?.toString());
            } finally {
              Sentry.setUser({
                email: userPayload.user.email,
                username: userPayload.user.uid,
              });
              logEvent({
                name: 'login',
                params: {
                  method: userInfo?.provider?.toLowerCase() ?? 'social',
                },
              });
              trackEvent(MIXPANEL_EVENTS.USER_LOGGED_IN, {
                method: userInfo?.provider?.toLowerCase() ?? 'social',
              });
              setAuthToken(response.user.api_tokens[0].token);
            }
          }
        }
      })
      .catch((_err) => {});
  };

  const appleSignInWeb = async (response: any) => {
    try {
      const localResponse = await AsyncStorage.getItem('appleUserInfo');
      let decodeEmail = '';

      const token = response.authorization.id_token;
      const decoded = jwt_decode<{ sub: string; email?: string }>(token);
      if (decoded) {
        decodeEmail = decoded.email ?? '';
      }
      if (!decoded?.sub) {
        Alert.alert('TaskTag', t('onBoarding.error.appleSignInFailed'));
        return;
      }

      const userData = localResponse ? JSON.parse(localResponse) : null;
      const userInfoPayload = {
        id: decoded.sub,
        email: response?.user?.email || userData?.user?.email || decodeEmail,
        given_name:
          response?.user?.name?.firstName || userData?.user?.name?.firstName,
        family_name:
          response?.user?.name?.lastName || userData?.user?.name?.lastName,
        provider: 'Apple',
        // TA-10975: the raw token, not the decoded claims — only this is
        // what the backend can still verify server-side.
        identity_token: token,
      };
      if (response.user?.email) {
        await AsyncStorage.setItem('appleUserInfo', JSON.stringify(response));
        setUserDetail && setUserDetail(userInfoPayload);
      } else if (response) {
        setUserDetail && setUserDetail(userInfoPayload);
      } else {
        Alert.alert('TaskTag', t('onBoarding.error.emailNotFound'));
      }
    } catch (e) {
      console.log('An error happened, ', e?.toString());
    } finally {
      setAppleAuthLoading(false);
    }
  };

  useEffect(() => {
    setTimeout(() => setShowMessage(false), 5000);
  }, []);

  const logIn = async (
    email: string,
    password: string,
    setErrors: (errors: FormikErrors<typeof initialValues>) => void
  ) => {
    await AsyncStorage.setItem('keepSignedIn', keepsigned ? 'true' : 'false');
    window.sessionStorage.setItem('setKeepSignedIn', '1');

    const login = `/api/v3/auth.json`;
    const loginPayload = {
      email: email.trim().toLowerCase(),
      password: password,
      firebase_token: deviceToken,
      platform: 'web',
    };

    const authEndpoint = `${apiConfig.apiUrl}${login}`;

    await fetch(authEndpoint, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(loginPayload),
    })
      // TA-10842: parseAuthResponse short-circuits a 429 throttle into a
      // handled { error } message instead of parsing the (often empty) body
      // as if it were a normal response.
      .then((response) => parseAuthResponse(response, t))
      .then(async (response: any) => {
        if (response?.error) {
          setErrors({
            password: response.error,
          });
        } else {
          try {
            await SecurityStorage.setItem('authToken', response.token);

            logEvent({ name: 'login', params: { email } });
            trackEvent(MIXPANEL_EVENTS.USER_LOGGED_IN, { method: 'email' });
          } catch (e) {
            // saving error
            console.log(e);
          } finally {
            Sentry.setUser({ email: email, username: email });
            // An existing account accepts a generic Contacts invite through
            // acceptUserInvite in AppStack, not through sign-up (TA-11035).
            stashGenericInvitationForLogin();
            setAuthToken(response.token);
          }
        }
      })
      .catch((err) => {
        console.log('err', err);
        setErrors({
          password: 'network error',
        });
      });
  };

  const buildForm = () => (
    <Formik
      validateOnMount={true}
      initialValues={initialValues}
      onSubmit={(values, { setErrors }) =>
        logIn(values.email, values.password, setErrors)
      }
      validate={(values) => {
        if (values.email) {
          setForgotPasswordError('');
        }
        return {};
      }}
      validationSchema={authSchema}>
      {({
        submitForm,
        values,
        isValid,
        isSubmitting,
        validateField,
        errors,
        setFieldTouched,
      }) => {
        const forgotPassword = async () => {
          setResetLoadingState(true);
          const result = await requestPasswordReset(values.email);
          setResetLoadingState(false);

          if (result === 'unavailable') {
            setForgotPasswordError(t('shared:cloudflareEdgeErrorMsg'));
            return;
          }

          // TA-10679: both of these are independent of whether the address has
          // an account — the rate limit is keyed on the address, and whether a
          // string parses as an email is decidable client-side — so surfacing
          // them leaks nothing, and beats sending the user to an inbox no mail
          // was sent to.
          if (result === 'throttled') {
            setForgotPasswordError(t('shared:tooManyResetAttempts'));
            return;
          }

          if (result === 'invalid') {
            setForgotPasswordError(t('shared:checkTheEmailAddress'));
            return;
          }

          // TA-2592: navigate to the confirmation screen for every address the
          // form accepts, registered or not. Branching on the backend's
          // `success` flag here told an attacker which emails have accounts.
          setForgotPasswordError('');
          navigation.navigate('forgot-password', {
            email: values.email.trim(),
          });
        };
        return (
          <Box backgroundColor='grey01' height='100%'>
            <Box alignItems='center' justifyContent='center' height='100%'>
              <Box
                alignItems='center'
                borderRadius='m'
                backgroundColor='white'
                style={{
                  paddingVertical: 32,
                  paddingHorizontal: 40,
                  shadowColor: 'black',
                  shadowOpacity: 0.05,
                  shadowOffset: { width: 0, height: 25 },
                  shadowRadius: 25,
                  position: 'absolute',
                  width: 492,
                  overflow: 'hidden',
                }}>
                <Icon name='Logo' width={142} height={43} />
                <Text
                  style={{ marginTop: 42, marginBottom: 26 }}
                  variant='heading2'
                  color='black'>
                  {t('onBoarding.modal.welcomeback')}
                </Text>

                <Box flex={1}>
                  <Box marginBottom='l'>
                    <Text
                      variant='labelSmall'
                      marginBottom='xs'
                      style={{ lineHeight: 16 }}
                      color='textPrimary'>
                      {t('shared:email')}
                    </Text>
                    <Box
                      width='100%'
                      pointerEvents={isSubmitting ? 'none' : 'auto'}>
                      <Field
                        component={TextField}
                        innerRef={emailInputRef}
                        placeholder='example@site.com'
                        placeholderTextColor='grey04'
                        marginBottom='none'
                        name='email'
                        textInputProps={{
                          autoCapitalize: 'none',
                          autoCorrect: false,
                          keyboardType: 'email-address',
                          style: {
                            color: isSubmitting
                              ? theme.colors.grey04
                              : theme.colors.black,
                          },
                        }}
                        customBackgroundColor={
                          isSubmitting ? theme.colors.grey01 : undefined
                        }
                        editable={!isSubmitting}
                        accessibilityLabel={t('shared:email')}
                        validateOnChange={false}
                        onFocus={() => {
                          // pointerEvents='none' blocks mouse-click focus
                          // while locked; this bounces back keyboard-Tab
                          // focus, which pointerEvents doesn't affect.
                          if (isSubmitting) emailInputRef.current?.blur();
                        }}
                        onKeyPress={({ nativeEvent: { key } }) => {
                          if (key === 'Enter' && isValid) {
                            emailInputRef.current?.blur();
                            passwordInputRef.current?.blur();
                            submitForm();
                          }
                        }}
                      />
                    </Box>
                    {forgotPasswordError != '' && (
                      <Box
                        flexDirection='row'
                        alignItems='center'
                        marginTop='xs'
                        marginRight='m'>
                        <Icon
                          name='AlertTriangle'
                          color='alertRed'
                          variant='s'
                          marginRight='xxs'
                        />
                        <Text variant='error'>{forgotPasswordError}</Text>
                      </Box>
                    )}
                  </Box>
                  <Box flexDirection='row' marginBottom='xs'>
                    <Text
                      variant='labelSmall'
                      style={{ lineHeight: 16 }}
                      color='textPrimary'>
                      {t('shared:password')}
                    </Text>
                    <Box flex={1}></Box>
                    <TouchableOpacity
                      onPress={async () => {
                        setFieldTouched('email', true);
                        validateField('email');
                        if (!errors.email) forgotPassword();
                      }}
                      disabled={resetLoadingState || isSubmitting}
                      accessibilityLabel={t('shared:forgotPassword')}>
                      <Text variant='webBodySecondary' color='greenSecondary'>
                        {t('shared:forgotPassword')}
                      </Text>
                    </TouchableOpacity>
                    {resetLoadingState && (
                      <Box marginLeft='s'>
                        <Spinner
                          size={16}
                          strokeWidth={2}
                          color={theme.colors.textPrimary}
                        />
                      </Box>
                    )}
                  </Box>
                  <Box
                    width='100%'
                    pointerEvents={isSubmitting ? 'none' : 'auto'}>
                    <Field
                      component={TextField}
                      innerRef={passwordInputRef}
                      placeholder='Enter password'
                      placeholderTextColor='grey04'
                      name='password'
                      isLarge
                      suffix={
                        <Icon
                          accessibilityLabel={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                          name={showPassword ? 'Eye' : 'EyeOff'}
                          variant='l'
                          color='textPrimary'
                          onPress={() => setShowPassword(!showPassword)}
                        />
                      }
                      textInputProps={{
                        autoCapitalize: 'none',
                        autoCorrect: false,
                        secureTextEntry: !showPassword,
                        color: isSubmitting
                          ? theme.colors.grey04
                          : theme.colors.black,
                      }}
                      customBackgroundColor={
                        isSubmitting ? theme.colors.grey01 : undefined
                      }
                      editable={!isSubmitting}
                      accessibilityLabel={t('shared:password')}
                      validateOnChange={true}
                      onFocus={() => {
                        // pointerEvents='none' blocks mouse-click focus
                        // while locked; this bounces back keyboard-Tab
                        // focus, which pointerEvents doesn't affect.
                        if (isSubmitting) passwordInputRef.current?.blur();
                      }}
                      onKeyPress={({ nativeEvent: { key } }) => {
                        if (key === 'Enter' && isValid) {
                          emailInputRef.current?.blur();
                          passwordInputRef.current?.blur();
                          submitForm();
                          return;
                        }
                      }}
                    />
                  </Box>
                  <TouchableOpacity
                    disabled={isSubmitting}
                    onPress={() => {
                      setKeepsigned(!keepsigned);
                    }}>
                    <Box
                      accessibilityLabel={t('onBoarding.modal.keepMeSignedIn')}
                      flexDirection='row'
                      alignItems='center'
                      justifyContent='flex-start'
                      mb='l'>
                      <CheckBoxButton checked={keepsigned} />
                      <Text
                        marginLeft='xs'
                        variant='typoMetadata'
                        color='textPrimary'>
                        {t('onBoarding.modal.keepMeSignedIn')}
                      </Text>
                    </Box>
                  </TouchableOpacity>
                  <Button
                    onPress={async () => {
                      emailInputRef.current?.blur();
                      passwordInputRef.current?.blur();
                      submitForm();
                    }}
                    marginBottom='l'
                    fullWidth
                    isSmall={true}
                    height={48}
                    disabled={!isValid}
                    isLoading={isSubmitting}
                    backgroundColor={isValid ? 'black' : 'grey02'}
                    btnTextColor={isValid ? 'white' : 'grey04'}
                    accessibilityLabel={t('onBoarding.logIn')}>
                    <Text
                      variant='labelSmall'
                      color={isValid ? 'white' : 'grey04'}
                      style={{ lineHeight: 16 }}>
                      {t('onBoarding.logIn')}
                    </Text>
                  </Button>
                  <Box
                    flexDirection='row'
                    mb='l'
                    alignItems='center'
                    style={{
                      height: 16,
                    }}>
                    <Box
                      flex={1}
                      backgroundColor='grey04'
                      style={{
                        height: 1,
                        marginRight: 23,
                      }}></Box>

                    <Text variant='webBodySecondary' color='grey04'>
                      {t('onBoarding.modal.orLoginWith')}
                    </Text>
                    <Box
                      flex={1}
                      backgroundColor='grey04'
                      style={{
                        height: 1,
                        marginLeft: 23,
                      }}></Box>
                  </Box>

                  <Box flexDirection='row' mb='l'>
                    <Box mr='xs' width={202}>
                      <Button
                        onPress={() => {
                          prompt();
                        }}
                        prefix={
                          <Icon
                            variant='l'
                            name='Google'
                            color='textPrimary'
                            accessibilityLabel='googleIcon'
                          />
                        }
                        isSmall={true}
                        height={48}
                        fullWidth
                        prefixMarginRight='xs'
                        disabled={loading || isSubmitting}
                        isLoading={googleAuthLoading}
                        borderWidth={1}
                        borderColor='grey03'
                        backgroundColor='white'
                        accessibilityLabel={t('onBoarding.modal.google')}
                        alignTextLeft={false}>
                        <Text variant='webBodySecondary' color='black'>
                          {t('onBoarding.modal.google')}
                        </Text>
                      </Button>
                    </Box>
                    <Box width={202}>
                      <AppleSignin
                        /** Auth options passed to AppleID.auth.init()  */
                        authOptions={{
                          clientId: process.env.APPLE_CLIENT_ID_WEB,
                          scope: 'name email',
                          redirectURI: process.env.APPLE_REDIRECT_UI,
                          state: '',
                          nonce: 'nonce',
                          usePopup: true,
                        }}
                        render={(props: any) => (
                          <Button
                            fullWidth
                            isSmall={true}
                            height={48}
                            borderWidth={1}
                            borderColor='grey03'
                            backgroundColor='white'
                            alignTextLeft={false}
                            prefixMarginRight='xs'
                            accessibilityLabel={t('onBoarding.modal.apple')}
                            prefix={
                              <Icon
                                variant='l'
                                name='Apple'
                                color='textPrimary'
                                accessibilityLabel={t('onBoarding.modal.apple')}
                              />
                            }
                            {...props}
                            disabled={loading || isSubmitting}
                            isLoading={appleAuthLoading}
                            onClick={(e: unknown) => {
                              if (appleAuthLoading) return;
                              setAppleAuthLoading(true);
                              props.onClick?.(e);
                            }}>
                            <Text variant='webBodySecondary' color='black'>
                              {t('onBoarding.modal.apple')}
                            </Text>
                          </Button>
                        )}
                        uiType='light'
                        className='apple-auth-btn'
                        onSuccess={(response: any) => appleSignInWeb(response)} // default = undefined
                        onError={(error: any) => {
                          console.error('error', error);
                          setAppleAuthLoading(false);
                        }}
                      />
                    </Box>
                  </Box>

                  <Box alignItems='center' my='xxxs'>
                    <Text variant='webBodySecondary' color='black'>
                      {t('onBoarding.modal.dontHaveAnAccount')}{' '}
                      <Text
                        accessibilityLabel={t('onBoarding.signUp')}
                        variant='webBodySecondary'
                        color='greenSecondary'
                        onPress={() => {
                          navigation.navigate('register');
                        }}>
                        {t('onBoarding.signUp')}
                      </Text>
                    </Text>
                  </Box>
                  <Box alignItems='center' my='s'>
                    <Text variant='webBodySecondary' color='black'>
                      {t('onBoarding.modal.needMoreHelp')}{' '}
                      <Text
                        accessibilityLabel={t('onBoarding.modal.bookDemo')}
                        variant='webBodySecondary'
                        color='greenSecondary'
                        onPress={() =>
                          Linking.openURL('https://portal.tasktag.com/demo')
                        }>
                        {t('onBoarding.modal.bookDemo')}
                      </Text>
                    </Text>
                  </Box>
                </Box>
              </Box>
              {showMessage && (
                <Box
                  backgroundColor='black'
                  borderRadius='xs'
                  px='s'
                  py='xs'
                  style={{
                    opacity: 0.8,
                    position: 'absolute',
                    bottom: 40,
                  }}>
                  <Text variant='webSmall' color='white'>
                    {t('models:onBoarding.modal.thePasswordHasBeenUpdated')}
                  </Text>
                </Box>
              )}
            </Box>
            {showProviderModal && (
              <ConfirmModal
                showModal={showProviderModal}
                onClose={() => setShowProviderModal(false)}
                onPress={() => setShowProviderModal(false)}
                buttonText={t('onBoarding.error.okPress')}
                title={t('onBoarding.error.title')}
                message={t(
                  'onBoarding.error.userAlreadyRegisteredWithProvider',
                  {
                    provider: errorName,
                  }
                )}
              />
            )}
          </Box>
        );
      }}
    </Formik>
  );

  return (
    <>
      <Helmet>
        <meta
          name='description'
          content='Log in to your TaskTag account to access your projects, manage tasks, and collaborate with your team in real time.'
        />
      </Helmet>
      {buildForm()}
    </>
  );
};
